import { type AppProps } from 'next/app';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { CacheProvider } from '@emotion/react';
import { createEmotionCache } from '@/utils/emotion-cache';
import UniversalCookies from 'universal-cookie';
import * as Sentry from '@sentry/nextjs';
import Layout from '@/components/layout';
import { getSpVisitorId } from '@/utils/sp-visitor-id';
import { getPcid } from '@/utils/pcid';
import { IViewDefinition } from '@/components/authored-page-routes/authored-page-routes.interface';
import { TKamFlags } from '@/store/kameleoonFlags/kameleoonFlags.interface';
import { TLaunchdarklyFlags } from '@/store/launchdarklyFlags/launchdarklyFlags.interface';
import GlobalState from '@/components/global-state';
import { initializeAnalytics, initializeSnowplowTracker } from '@/utils/analytics/analytics-utils';
import { initializeSalesfloorAccessibility, isAndroidDevice, isPharmaprixBanner } from '@/utils';
import { useUpdateAppBannerStyles } from '@/hooks';
import { fireworkTrackPageView, useFireworkAnalytics } from '@/utils/firework/firework-analytics';
import { banners, COOKIE_CONSENT, GCP_BUCKET_URL } from '@/data/constants';
import { ADA_ENABLE_ROUTES } from '@/utils/routes';
import {
  PERFECT_EXPERIENCE_MAX_DURATION_MS,
  SNOWPLOW_PE_CLIENT_FLAG,
  SnowplowPerfectExperienceTracker
} from '@iceberg/report-perfect-experience-analytics';

import '../styles/globals.scss';
import '../styles/tailwind.css';
import '@iceberg/themes/files/css/globals.css';
import { FireworkProvider } from '@/store/firework/fireworkStore';
import { FireworkInit } from '@/components/firework/firework-init';
import { KAMELEOON_FEATURE_FLAGS } from '@/utils/kameleoon/constants';
import { SSRThemeProvider } from '@ldp-web/component-library/providers/ssrthemeprovider';
import { ThemeFontsLoader } from '@ldp/theme-font-loader';
import { useDeviceLayoutCookie } from '@/utils/use-device-layout-cookie';

export interface PreRenderProps {
  viewDefinitionResults: {
    viewDefinition: IViewDefinition;
    currentUrlBase: string;
  } | null;
  themeTokens: object | null;
  cartData: object | null;
  kamFlags: TKamFlags | null;
  launchdarklyFlags: TLaunchdarklyFlags | null;
  enableSSR?: boolean;
}

async function enableMocking() {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_API_MOCKING === 'enabled') {
    const { worker } = await import('../mocks/browser');
    return worker.start({ onUnhandledRequest: 'bypass' });
  }
}

const cookieStore = new UniversalCookies();

function MyApp({ Component, pageProps }: AppProps<PreRenderProps>) {
  const router = useRouter();
  // One cache for app lifetime; recreating on every render would break style accumulation and hydration
  const [emotionCache] = useState(createEmotionCache);
  enableMocking();
  const { cartData, viewDefinitionResults, kamFlags, launchdarklyFlags, themeTokens, enableSSR = false } = pageProps;
  const { SDM_ID, PHX_ID, IS_COOKIE_CONSENT } = COOKIE_CONSENT;
  const domainScriptId = isPharmaprixBanner() ? PHX_ID : SDM_ID;
  const isCookieConsented = !!cookieStore.get(IS_COOKIE_CONSENT);

  // Apply banner style updates when the app banner is present
  useUpdateAppBannerStyles();

  useFireworkAnalytics();

  useDeviceLayoutCookie();

  // Initialize Snowplow tracker early to ensure it's ready before events are pushed and OTEL Browser
  useEffect(() => {
    // Dynamic import to prevent browser OpenTelemetry from loading on server-side
    import('@/otel/otel.browser').then(({ initBrowserOtel }) => {
      initBrowserOtel();
    });
    initializeSnowplowTracker();
  }, []);

  // Defer other analytics initialization to improve initial page load
  useEffect(() => {
    // Use requestIdleCallback if available, otherwise setTimeout to defer execution
    const initAnalytics = () => {
      initializeAnalytics();
      initializeSalesfloorAccessibility();
      // Set Snowplow visitor ID tag for Sentry client-side errors
      const spVisitorId = getSpVisitorId();
      if (spVisitorId) {
        Sentry.setTag('sp_visitor_id', spVisitorId);
      }

      // Set Sentry user if logged in
      const pcid = getPcid();
      if (pcid) {
        Sentry.setUser({ id: pcid });
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      requestIdleCallback(initAnalytics, { timeout: 2000 });
    } else {
      setTimeout(initAnalytics, 0);
    }
  }, []);

  // ENGAGE-4423: Add aria-hidden to chakra-ui toast regions for A11Y
  useEffect(() => {
    let count = 0;
    const interval = setInterval(() => {
      count += 1;
      const toastRegions = document.querySelectorAll('.chakra-portal [role=region]');
      if (count === 10 || toastRegions.length) {
        clearInterval(interval);
      }
      toastRegions.forEach((region) => {
        region.setAttribute('aria-hidden', 'true');
      });
    }, 2000);
    return () => {
      clearInterval(interval)
    }
  }, []);

  // ENGAGE-4692: Toggle ada-enabled class on body based on route to control Ada button visibility via CSS
  const isAdaRoute = ADA_ENABLE_ROUTES.some((path: string) => router?.asPath?.includes(path));
  useEffect(() => {
    if (typeof window === 'undefined') return;

    document.body.classList.toggle('ada-enabled', isAdaRoute);
  }, [isAdaRoute]);

  const isSnowPlowPEClientEnabled = () => {
    if (typeof window === "undefined") {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    return params.get(SNOWPLOW_PE_CLIENT_FLAG) === "true";
  };

  // Track page views
  useEffect(() => {
    // Get current page path
    const currentPath = router.pathname;

    // Track page view with correct page type
    switch (currentPath) {
      case '/shoppingbag':
        fireworkTrackPageView('shoppingbag');
        break;
      case '/purchase':
        fireworkTrackPageView('purchase');
        break;
    }
    // Track page view when route changes
  }, [router.pathname]); // Re-run when path changes

  const banner = isPharmaprixBanner() ? banners.PHARMAPRIX : banners.SHOPPERS;
  const isSnowplowPerfectExperienceEnabled = kamFlags?.[KAMELEOON_FEATURE_FLAGS.SNOWPLOW_PERFECT_EXPERIENCE_FLAG] ?? false;

  const lobConfig = {
    lob: "SDM",
    banner,
    maxDurationMs: PERFECT_EXPERIENCE_MAX_DURATION_MS,
  };
  if (
    (isSnowplowPerfectExperienceEnabled && banner) ||
    isSnowPlowPEClientEnabled()
  ) {
    SnowplowPerfectExperienceTracker.getInstance(lobConfig);
  }

  const appContent: React.ReactNode = (
    <GlobalState
      cartData={cartData ?? undefined}
      viewDefinitionResults={viewDefinitionResults ?? undefined}
      kamFlags={kamFlags ?? null}
      ldFlags={launchdarklyFlags ?? null}
    >
      <Layout viewDefinitionResults={viewDefinitionResults}>
        <Component {...pageProps} />
        {/* embed Firework carousel/grid widgets - lazy load to improve initial page load */}
        <Script
          className="optanon-category-C0003"
          type="text/plain" // text/plain default type for Quebec CCM
          strategy="afterInteractive"
          src="//asset.fwcdn3.com/js/embed-feed.js"
        />
        <Script
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          data-document-language="true"
          charSet="UTF-8"
          data-domain-script={domainScriptId}
          data-dLayer-name="analyticsLayer"
          async
        />
        {/* embed forter script for fraud detection - deferred to reduce TBT */}
        <Script
          id={process.env.NEXT_PUBLIC_FORTER_ID}
          strategy="afterInteractive"
          src={`${GCP_BUCKET_URL}/static/forter.js`}
        ></Script>
        {!isCookieConsented ? <Script id="opt-wrapper" src={`${GCP_BUCKET_URL}/static/opt-wrapper.js`} async /> : null}
        <Script id="gtm-script" strategy="lazyOnload">
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','analyticsLayer','GTM-TCJKP3F');`}
        </Script>
        <Script id="analytics-dataLayer" strategy="lazyOnload">
          {`window.dataLayer = window.dataLayer || [];`}
        </Script>
        {isAndroidDevice() && (
          <Script id="app-banner" strategy="lazyOnload" src={`${GCP_BUCKET_URL}/static/app-banner.js`} />
        )}
        <Script src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" strategy="lazyOnload" />
        {/*embed ada chatbot */}
        {isAdaRoute && (
          <Script id="__ada" strategy="lazyOnload" data-handle="sdm-lcl" src="https://static.ada.support/embed2.js" />
        )}
        <Script id="ad-blocker" strategy="lazyOnload">
          {`
            const googleAdUrl = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
            try {
              fetch(new Request(googleAdUrl)).catch((_) => dataLayer.push({ event: 'AdBlocker' }));
            } catch (e) {
              dataLayer.push({ event: 'AdBlocker' });
            }
          `}
        </Script>
      </Layout>
    </GlobalState>
  );

  return (
    <CacheProvider value={emotionCache}>
      <FireworkProvider>
        <FireworkInit />
        {enableSSR ? (
          <SSRThemeProvider baseTheme={themeTokens ?? {}} baseFont={12}>
            <ThemeFontsLoader />
            {appContent}
          </SSRThemeProvider>
        ) : (
          appContent
        )}
      </FireworkProvider>
    </CacheProvider>
  );
}

export default MyApp;
